// src/components/StackedBarChart.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ContextMenu from './ContextMenu';
import { jsonToCsv, downloadFile, DIM_FIELDS } from '../utils';
import Dropdown from './Dropdown';
import MultiDropdown from './MultiDropdown';

const HEX = ['#2ad1d1','#ffd166','#8e7cff','#5bd17a','#ff6e6e','#89a7ff','#f3a6ff','#b8f36e'];
const toRGBA = (hex, a=1)=>`rgba(${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)},${a})`;
const colorFor = i => HEX[i % HEX.length];
const isHistoric = t => String(t||'').toLowerCase().startsWith('histor');

const BREAKDOWN_OPTIONS = ['None', 'Input', 'Mineral', 'Region', 'Country', 'Form', 'Scenario'];

export default function StackedBarChart({ rows, allRows, noCountryFilter, filters, onFiltersChange, settings, onSettingsChange }) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [legendItems, setLegendItems] = useState([]);
  const [xAxis, setXAxis] = useState('Year');

  // Use settings from props
  const { breakdownBy } = settings;
  const setBreakdownBy = (v) => onSettingsChange({ ...settings, breakdownBy: v });
  const overlays = settings.overlays || [];
  const setOverlays = (v) => onSettingsChange({ ...settings, overlays: v });

  const {
    labels, seriesNames, histData, projData, isStacked, units, overlayData, isBreakdownEmpty
  } = useMemo(()=>{
    const valueField = 'Avg Value';
    let chartRows = rows;
    let units;
    const isCost = filters.value_type === 'Cost';
    const isPrice = filters.value_type === 'Price';
    
    if (isCost || isPrice) {
        units = 'USD/T';
        // Filter by Value Type instead of Unit Type for consistency
        let costOrPriceRows = rows.filter(r => r['Value Type'] === filters.value_type);
        // Accept both "USD/T" and "$/t" formats
        if (costOrPriceRows.some(r => r.Units === 'USD/T' || r.Units === '$/t')) {
          chartRows = costOrPriceRows.filter(r => r.Units === 'USD/T' || r.Units === '$/t');
        } else {
          chartRows = costOrPriceRows;
        }
    } else {
        units = 'T';
        chartRows = rows.filter(r => r['Value Type'] === filters.value_type);
    }
    
    const isStacked = breakdownBy === 'Input';
    const labels = Array.from(new Set(chartRows.map(r=>r[xAxis]).filter(Boolean))).sort((a,b)=> isNaN(a) || isNaN(b) ? String(a).localeCompare(String(b)) : a - b);

    let dataRows = chartRows;
    
    // Only exclude total-like values for the 'Input' breakdown
    if (breakdownBy === 'Input') {
      dataRows = dataRows.filter(r => {
        const breakdownValue = r.Input || '';
        const lowerValue = String(breakdownValue).toLowerCase();
        return !['total', 'unspecified', 'total cost'].includes(lowerValue);
      });
    }

    let seriesNames, histData, projData;
    
    const createAccumulator = () => labels.map(() => ({ sum: 0, count: 0 }));

    if (breakdownBy === 'None') {
        seriesNames = [filters.value_type];
        const histAccumulator = createAccumulator();
        const projAccumulator = createAccumulator();

        const addValue = (r) => {
            const xIdx = labels.indexOf(r[xAxis]);
            if (xIdx === -1) return;
            const v = Number(r[valueField]); 
            if (!Number.isFinite(v) || v <= 0) return;
            
            if (isHistoric(r.Type)) {
              histAccumulator[xIdx].sum += v;
              histAccumulator[xIdx].count++;
            } else {
              projAccumulator[xIdx].sum += v;
              projAccumulator[xIdx].count++;
            }
        };
        
        let rowsToProcess;
        if (isCost || isPrice) {
            // Prefer rows that explicitly state they are totals
            const totalRows = dataRows.filter(r => ['total', 'total cost'].includes(String(r.Input).toLowerCase()));
            if (totalRows.length > 0) {
                rowsToProcess = totalRows;
            } else {
                // Otherwise, use the old logic of finding rows with no Input specified
                rowsToProcess = dataRows.filter(r => r.Input == null);
            }
        } else {
            rowsToProcess = dataRows;
        }
        rowsToProcess.forEach(addValue);
        
        histData = [histAccumulator.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null)];
        projData = [projAccumulator.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null)];

    } else {
      let breakdownRows = dataRows;
      if (isCost || isPrice) {
        breakdownRows = dataRows.filter(r => r.Input !== 'Total Cost');
      }
      
      // For price data, we need to calculate averages instead of sums for determining top categories
      const categoryStats = new Map();
      breakdownRows.forEach(r => {
        let key = r[breakdownBy] ?? 'Unknown';
        // Group "Total" with null/undefined values
        if (String(key).toLowerCase() === 'total' || key === 'Unknown') {
          key = 'Total';
        }
        const value = Number(r[valueField]) || 0;
        
        if (!categoryStats.has(key)) {
          categoryStats.set(key, { sum: 0, count: 0 });
        }
        const stats = categoryStats.get(key);
        stats.sum += value;
        stats.count += 1;
      });
      
      // Calculate average for each category and sort by average
      const categoryAverages = Array.from(categoryStats.entries()).map(([key, stats]) => ({
        key,
        average: stats.count > 0 ? stats.sum / stats.count : 0
      }));
      
      const top5 = categoryAverages
        .sort((a, b) => b.average - a.average)
        .slice(0, 5)
        .map(d => d.key);
      const otherSet = new Set(Array.from(categoryStats.keys()).filter(k => !top5.includes(k)));
      seriesNames = [...top5];
      if (otherSet.size > 0) seriesNames.push('Other');

      const histAccumulators = seriesNames.map(() => createAccumulator());
      const projAccumulators = seriesNames.map(() => createAccumulator());

      for (const r of breakdownRows) {
        const xIdx = labels.indexOf(r[xAxis]);
        if (xIdx === -1) continue;
        const v = Number(r[valueField]); 
        if (!Number.isFinite(v) || v <= 0) continue;

        const historic = isHistoric(r.Type);
          let key = r[breakdownBy] ?? 'Unknown';
        // Group "Total" with null/undefined values
        if (String(key).toLowerCase() === 'total' || key === 'Unknown') {
          key = 'Total';
        }
          const inputName = otherSet.has(key) ? 'Other' : key;
          const iIdx = seriesNames.indexOf(inputName);
          if (iIdx === -1) continue;
        if (historic) {
          histAccumulators[iIdx][xIdx].sum += v;
          histAccumulators[iIdx][xIdx].count++;
        } else {
          projAccumulators[iIdx][xIdx].sum += v;
          projAccumulators[iIdx][xIdx].count++;
        }
      }
      
      histData = histAccumulators.map(series => series.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null));
      projData = projAccumulators.map(series => series.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null));
    }

    const overlayData = [];
    if (overlays && overlays.length > 0) {
      for (const overlay of overlays) {
        if (overlay === 'Benchmark') {
          // Handle benchmark overlay - moved from automatic calculation above
          if (isCost || isPrice) {
            const mineralTotals = new Map();
            chartRows.forEach(r => {
              const mineral = r.Mineral;
              if (!mineral) return;
              const value = Number(r[valueField]) || 0;
              mineralTotals.set(mineral, (mineralTotals.get(mineral) || 0) + value);
            });
            const top5Minerals = Array.from(mineralTotals.entries())
                                      .sort((a,b) => b[1] - a[1])
                                      .slice(0, 5)
                                      .map(d => d[0]);
            
            let chartAllRows = allRows.filter(r => r['Value Type'] === filters.value_type);
            // Accept both "USD/T" and "$/t" formats
            if (chartAllRows.some(r => r.Units === 'USD/T' || r.Units === '$/t')) {
              chartAllRows = chartAllRows.filter(r => r.Units === 'USD/T' || r.Units === '$/t');
            }

            const benchmarkCostRows = chartAllRows.filter(r => {
              const input = r.Input || '';
              const lowerInput = input.toLowerCase();
              return r.Input != null && lowerInput !== 'total cost';
            });

            for (const mineral of top5Minerals) {
                const mineralRows = benchmarkCostRows.filter(r => r.Mineral === mineral);
                const histAccumulator = createAccumulator();
                const projAccumulator = createAccumulator();

                mineralRows.forEach(r => {
                    const xIdx = labels.indexOf(r[xAxis]);
                    if (xIdx === -1) return;
                    const v = Number(r[valueField]);
                    if (!Number.isFinite(v)) return;

                    if (isHistoric(r.Type)) {
                        histAccumulator[xIdx].sum += v;
                        histAccumulator[xIdx].count++;
                    } else {
                        projAccumulator[xIdx].sum += v;
                        projAccumulator[xIdx].count++;
                    }
                });

                const hist = histAccumulator.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null);
                const proj = projAccumulator.map(a => a.count > 0 ? (isCost || isPrice ? a.sum / a.count : a.sum) : null);

                if (hist.some(v => v !== null) || proj.some(v => v !== null)) {
                  overlayData.push({ 
                    hist, 
                    proj, 
                    units: 'USD/T', 
                    name: `${mineral} (Benchmark)`,
                    type: 'benchmark'
                  });
                }
            }
          }
        } else {
          // Handle regular overlays (Cost, Price, Supply, Demand)
          const isOverlayCost = overlay === 'Cost';
          const isOverlayPrice = overlay === 'Price';
          const overlayUnits = (isOverlayCost || isOverlayPrice) ? 'USD/T' : 'T';

          let overlayRows = allRows.filter(r => r['Value Type'] === overlay);
          if (isOverlayCost || isOverlayPrice) {
              // Filter by Value Type instead of Unit Type
              let costOrPriceRows = overlayRows.filter(r => r['Value Type'] === overlay);
              // Accept both "USD/T" and "$/t" formats
              if (costOrPriceRows.some(r => r.Units === 'USD/T' || r.Units === '$/t')) {
                overlayRows = costOrPriceRows.filter(r => r.Units === 'USD/T' || r.Units === '$/t');
                    } else {
                overlayRows = costOrPriceRows;
              }
          }

          const histAccumulator = createAccumulator();
          const projAccumulator = createAccumulator();

          const addValue = (r) => {
              const xIdx = labels.indexOf(r[xAxis]);
              if (xIdx === -1) return;
              const v = Number(r[valueField]);
              if (!Number.isFinite(v) || v <= 0) return;

              if (isHistoric(r.Type)) {
                histAccumulator[xIdx].sum += v;
                histAccumulator[xIdx].count++;
              } else {
                projAccumulator[xIdx].sum += v;
                projAccumulator[xIdx].count++;
              }
          };
          
          // Try being less restrictive - don't filter by Input for overlays
          const rowsToProcess = overlayRows;
          rowsToProcess.forEach(addValue);

          const hist = histAccumulator.map(a => a.count > 0 ? (isOverlayCost || isOverlayPrice ? a.sum / a.count : a.sum) : null);
          const proj = projAccumulator.map(a => a.count > 0 ? (isOverlayCost || isOverlayPrice ? a.sum / a.count : a.sum) : null);

            if (hist.some(v => v !== null) || proj.some(v => v !== null)) {
            overlayData.push({ hist, proj, units: overlayUnits, name: overlay });
          }
            }
        }
    }

    const hasData = histData.some(series => series.some(value => value !== null)) || 
                    projData.some(series => series.some(value => value !== null));
    const isBreakdownEmpty = !hasData && breakdownBy !== 'None';

    return {
      labels, seriesNames, histData, projData, isStacked, units, overlayData, isBreakdownEmpty
    };
  }, [rows, allRows, breakdownBy, filters.value_type, noCountryFilter, xAxis, settings.overlays]);

  useEffect(() => {
    if (isBreakdownEmpty) {
      onSettingsChange({ ...settings, breakdownBy: 'None' });
    }
  }, [isBreakdownEmpty, onSettingsChange, settings]);

  useEffect(()=>{
    if (!ref.current) return;
    chartRef.current?.destroy();

    const datasets = [];
    const newLegendItems = [];
    const isStacked = breakdownBy === 'Input';

    seriesNames.forEach((name, i)=>{
      const base = colorFor(i);
      newLegendItems.push({ label: name, color: base, type: 'bar' });
      datasets.push({
        type:'bar', label: `${name} (Hist)`,
        ...isStacked && { stack: 'hist' },
        backgroundColor: toRGBA(base, 0.8), borderColor: base, borderWidth:0, data: histData[i],
        ...(!isStacked && { categoryPercentage: 0.85, barPercentage: 1.0 }),
      });
      datasets.push({
        type:'bar', label: `${name} (Proj)`,
        ...isStacked && { stack: 'proj' },
        backgroundColor: toRGBA(base, 0.08), borderColor: base, borderWidth:2, borderDash:[6,4], data: projData[i],
        ...(!isStacked && { categoryPercentage: 0.85, barPercentage: 1.0 }),
      });
    });

    const y1Units = units;
    const overlayUnits = overlayData.map(o => o.units);
    const y2Units = overlayUnits.find(u => u !== y1Units);
    const needsY2 = !!y2Units;

    if (overlayData) {
      overlayData.forEach((o, i) => {
          const color = colorFor(seriesNames.length + i);
        newLegendItems.push({ label: o.name, color, type: 'line' });
          datasets.push({
              type: 'line',
            label: `${o.name} (Hist)`,
            data: o.hist,
              borderColor: color,
              backgroundColor: 'transparent',
              borderWidth: 2, pointRadius: 2, tension: 0.2,
              spanGaps: true,
            yAxisID: o.units !== y1Units ? 'y2' : 'y',
          });
          datasets.push({
              type: 'line',
            label: `${o.name} (Proj)`,
            data: o.proj,
              borderColor: color,
              backgroundColor: 'transparent',
              borderWidth: 2, pointRadius: 2, tension: 0.2, borderDash: [6, 4],
              spanGaps: true,
            yAxisID: o.units !== y1Units ? 'y2' : 'y',
          });
      });
    }

    const yAxes = {
      y:{ stacked: isStacked, title:{display:true, text:`Avg Value (${units||''})`}, grid:{color:'#222'}, ticks:{color:'#aaa'} },
    };
    if (needsY2) {
      yAxes.y.title.text = `Avg Value (${y1Units || ''})`;
      const y2Label = y2Units === 'USD/T' ? 'Avg Cost' : 'Avg Amount';
      yAxes.y2 = {
        position: 'right',
        title: { display: true, text: `${y2Label} (${y2Units})` },
        grid: { display: false },
        ticks: { color: '#aaa' }
      };
    }

    setLegendItems(newLegendItems);

    const ctx = ref.current.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type:'bar',
      data:{ labels, datasets },
      options:{
        responsive:true,
        scales:{
          x:{
            stacked: isStacked,
            offset:true,
            title:{display:true, text:xAxis},
            grid:{color:'#222'},
            ticks:{color:'#aaa'}
          },
          ...yAxes
        },
        plugins:{
          legend:{ display:false },
          tooltip:{
            mode:'index', intersect:false,
            filter:(ctx)=> ctx.raw !== null && Number(ctx.raw) > 0,
            callbacks:{
              title: (items)=>{
                if (!items || !items.length) return '';
                return `${xAxis}: ${items[0].label}`;
              },
              label: (ctx)=> {
                const label = ctx.dataset.label.replace(/\s\((Hist|Proj)\)$/, '').replace(/\s\(Benchmark (Hist|Proj)\)$/, ' (Benchmark)');
                return `${label}: ${Number(ctx.raw).toLocaleString(undefined,{maximumFractionDigits:2})}`;
              },
              footer: (items)=>{
                if (!items?.length) return '';
                const barItems = items.filter(item => item.dataset.type === 'bar');
                if (breakdownBy === 'None' || barItems.length <= 1) return '';

                const total = barItems.reduce((s, item) => s + (Number(item.raw) || 0), 0);
                return `Total: ${total.toLocaleString(undefined,{maximumFractionDigits:2})}`;
              }
            },
            footerFont:{ weight:'bold' }
          }
        }
      }
    });

    return ()=>chartRef.current?.destroy();
  }, [seriesNames, labels, histData, projData, units, breakdownBy, xAxis, overlayData]);

  const handleContextMenu = (e) => {
    e.preventDefault();
    if (!rows?.length) return;

    const getChartDataAsObjects = () => {
      const data = [];
      const allLabels = labels.map(String);

      seriesNames.forEach((input, i) => {
        const histRow = { Category: `${input} (Historical)` };
        const projRow = { Category: `${input} (Projected)` };
        allLabels.forEach((label, lIdx) => {
            histRow[label] = histData[i][lIdx] || 0;
            projRow[label] = projData[i][lIdx] || 0;
        });
        if (Object.values(histRow).slice(1).some(v => v > 0)) data.push(histRow);
        if (Object.values(projRow).slice(1).some(v => v > 0)) data.push(projRow);
      });

      const totalCostHist = labels.map((_, lIdx) => histData.reduce((sum, series) => sum + (series[lIdx] || 0), 0));
      const totalCostProj = labels.map((_, lIdx) => projData.reduce((sum, series) => sum + (series[lIdx] || 0), 0));
      const totalHistRow = { Category: 'Total (Historical)' };
      const totalProjRow = { Category: 'Total (Projected)' };
      allLabels.forEach((label, lIdx) => {
          totalHistRow[label] = totalCostHist[lIdx] || 0;
          totalProjRow[label] = totalCostProj[lIdx] || 0;
      });
      if (Object.values(totalHistRow).slice(1).some(v => v > 0)) data.push(totalHistRow);
      if (Object.values(totalProjRow).slice(1).some(v => v > 0)) data.push(totalProjRow);

      if (overlayData) {
        overlayData.forEach(o => {
          const histRow = { Category: `${o.name} (Hist)` };
          const projRow = { Category: `${o.name} (Proj)` };
          allLabels.forEach((label, lIdx) => {
            histRow[label] = o.hist[lIdx] || 0;
            projRow[label] = o.proj[lIdx] || 0;
          });
          if (Object.values(histRow).slice(1).some(v => v > 0)) data.push(histRow);
          if (Object.values(projRow).slice(1).some(v => v > 0)) data.push(projRow);
        });
      }

      return data;
    };

    const downloadCSV = () => {
      const chartData = getChartDataAsObjects();
      if (chartData.length === 0) return;
      const headers = ['Category', ...labels.map(String)];
      const csv = jsonToCsv(chartData, headers);
      downloadFile(csv, 'chart-data.csv', 'text/csv;charset=utf-8;');
    };

    const copyCSV = () => {
      const chartData = getChartDataAsObjects();
      if (chartData.length === 0) return;
      const headers = ['Category', ...labels.map(String)];
      const csv = jsonToCsv(chartData, headers);
      navigator.clipboard.writeText(csv).catch(err => console.error('Failed to copy CSV: ', err));
    };

    const downloadPNG = () => {
        if (chartRef.current) {
            const url = chartRef.current.toBase64Image();
            const a = document.createElement('a');
            a.href = url;
            a.download = 'chart.png';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    };

    const copyPNG = () => {
        if (ref.current) {
            ref.current.toBlob(blob => {
                if (blob) {
                    navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
                        .catch(err => console.error('Failed to copy image: ', err));
                }
            });
        }
    };

    setContextMenu({
        x: e.clientX, y: e.clientY,
        options: [
            { label: 'Download data as CSV', action: downloadCSV },
            { label: 'Copy data to clipboard', action: copyCSV },
            { label: 'Download chart as PNG', action: downloadPNG },
            { label: 'Copy chart to clipboard', action: copyPNG },
        ]
    });
  };

  const barItems = legendItems.filter(item => item.type === 'bar');
  const lineItems = legendItems.filter(item => item.type === 'line');

  const availableValueTypes = useMemo(() => new Set(allRows.map(r => r['Value Type'])), [allRows]);

  const overlayOptions = useMemo(() => {
    const allOptions = ['Cost', 'Price', 'Supply', 'Demand', 'Benchmark'];
    return allOptions
      .filter(o => o !== filters.value_type)
      .map(o => ({
        value: o,
        label: o,
        disabled: (o === 'Supply' || o === 'Demand') && !availableValueTypes.has(o),
      }));
  }, [filters.value_type, availableValueTypes]);

  return (
    <div className="panel chart" onContextMenu={handleContextMenu}>
      <div className="panel-header">
        <div className="panel-title">
          <h3>CHART</h3>
        </div>
        <div className="control control-row">
          <label>X-Axis</label>
          <Dropdown value={xAxis} onChange={setXAxis} options={DIM_FIELDS} />
          <label>Y-Axis</label>
          <Dropdown 
            value={filters.value_type} 
            onChange={v => onFiltersChange({...filters, value_type: v})} 
            options={['Cost', 'Price', 'Supply', 'Demand']} 
            hideAll={true} 
          />
          <label>Overlay</label>
          <MultiDropdown 
            values={overlays} 
            onChange={setOverlays} 
            options={overlayOptions}
            placeholder="None"
            hideSearch={true}
          />
          <label>Breakdown by</label>
          <Dropdown value={breakdownBy} onChange={setBreakdownBy} options={BREAKDOWN_OPTIONS} hideAll={true} />
        </div>
      </div>
      <canvas ref={ref} height="140"></canvas>
      <div className="legend">
        {barItems.length > 0 && (
          <div className="legend-group">
            {barItems.map(({ label, color }) => (
              <span className="badge" key={label}>
                <span className="swatch" style={{ backgroundColor: color }}></span> {label}
              </span>
            ))}
          </div>
        )}
        {lineItems.length > 0 && (
          <div className="legend-group">
            {lineItems.map(({ label, color }) => (
              <span className="badge" key={label}>
                <span className="swatch line" style={{ backgroundColor: color }}></span> {label}
              </span>
            ))}
          </div>
        )}
      </div>
      {contextMenu && <ContextMenu {...contextMenu} onRequestClose={() => setContextMenu(null)} />}
    </div>
  );
}